import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Section, SectionRow } from './CertificateDetailPage';
import { getApiV1Url } from './config/api';
import { formatDate } from './utils/dateUtils';
import { Declaration, DeclarationBasis } from './types/declaration';
import { getDeclarationDetailPath, getDeclarationStatus, getFsaLink, getLegalBasis } from './utils/declarationUtils';

const buildShareLink = (basis: DeclarationBasis, regNumber: string | null) => {
    if (!regNumber || typeof window === 'undefined') {
        return '';
    }
    return `${window.location.origin}${getDeclarationDetailPath(basis, regNumber)}`;
};

const DeclarationDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { basis: basisParam, declNumber } = useParams<{ basis: string; declNumber: string }>();
    const basis = basisParam as DeclarationBasis;

    // React Router уже раскодирует параметры, повторный decodeURIComponent падает на номерах с "%"
    const decodedNumber = declNumber || null;

    const initialDeclarationState = (location.state as { declaration?: Declaration } | undefined)?.declaration;
    const initialDeclaration = initialDeclarationState && decodedNumber && initialDeclarationState.reg_number === decodedNumber ? initialDeclarationState : null;
    const [declaration, setDeclaration] = useState<Declaration | null>(initialDeclaration);
    const [loading, setLoading] = useState(!initialDeclaration);
    const [error, setError] = useState<string | null>(null);
    const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');

    useEffect(() => {
        if (!decodedNumber) {
            setError('Некорректный номер декларации.');
            setLoading(false);
            return;
        }

        if (declaration) return;

        // Открыли по прямой ссылке - запрашиваем только эту декларацию
        let cancelled = false;
        const fetchDeclaration = async () => {
            setLoading(true);
            setError(null);
            try {
                const access = localStorage.getItem('access');
                const response = await axios.get<Declaration[]>(getApiV1Url('DECLARATIONS'), {
                    params: { reg_number: decodedNumber, legal_basis: getLegalBasis(basis) },
                    headers: access ? { Authorization: `Bearer ${access}` } : undefined,
                });
                if (cancelled) return;
                const found = (response.data || []).find(item => item.reg_number === decodedNumber);
                if (found) {
                    setDeclaration(found);
                } else {
                    setError('Декларация не найдена. Возможно, данные были обновлены.');
                }
            } catch (err) {
                if (!cancelled) {
                    setError('Ошибка загрузки данных о декларации.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchDeclaration();
        return () => { cancelled = true; };
    }, [declaration, decodedNumber, basis]);

    const shareLink = useMemo(() => buildShareLink(basis, decodedNumber), [basis, decodedNumber]);

    const handleCopyLink = async () => {
        if (!shareLink) return;
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopyStatus('copied');
            setTimeout(() => setCopyStatus('idle'), 2000);
        } catch {
            setCopyStatus('error');
            setTimeout(() => setCopyStatus('idle'), 2000);
        }
    };

    const handleBack = () => {
        if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate(`/declarations/${basis}`);
        }
    };

    useEffect(() => {
        document.title = `Декларация ${getLegalBasis(basis)} — АПТС`;
    }, [basis]);

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
            }}>
                Загрузка данных декларации...
            </div>
        );
    }

    if (error || !declaration) {
        return (
            <div style={{ padding: 24 }}>
                <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 24,
                    maxWidth: 640,
                    margin: '0 auto',
                }}>
                    <h2 style={{ marginTop: 0 }}>Не удалось открыть декларацию</h2>
                    <p style={{ color: '#4b5563', lineHeight: 1.5 }}>{error || 'Данные отсутствуют.'}</p>
                    <button
                        onClick={() => navigate(`/declarations/${basis}`)}
                        style={{
                            padding: '10px 16px',
                            borderRadius: 6,
                            border: '1px solid #d1d5db',
                            background: '#fff',
                            cursor: 'pointer',
                        }}
                    >
                        К списку деклараций
                    </button>
                </div>
            </div>
        );
    }

    const fsaLink = getFsaLink(declaration);

    return (
        <div style={{ padding: '32px 24px', display: 'flex', justifyContent: 'center', background: '#f3f7fb', minHeight: '100vh' }}>
            <div style={{ width: '100%', maxWidth: 1200, display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{
                    background: '#0071bc',
                    borderRadius: 16,
                    padding: 32,
                    color: '#fff',
                    boxShadow: '0 24px 50px rgba(0, 113, 188, 0.35)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 16,
                    alignItems: 'center',
                }}>
                    <button
                        onClick={handleBack}
                        style={{
                            padding: '10px 20px',
                            borderRadius: 999,
                            border: '1px solid rgba(255,255,255,0.4)',
                            background: 'transparent',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 500,
                        }}
                    >
                        ← Назад
                    </button>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 260 }}>
                        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Декларация {getLegalBasis(basis)}</h1>
                        <span style={{ fontSize: 16, opacity: 0.95 }}>
                            {declaration.reg_number}
                        </span>
                        <span style={{ opacity: 0.9, fontSize: 14 }}>
                            Дата регистрации: {declaration.reg_date ? formatDate(declaration.reg_date) : 'Не указана'}
                        </span>
                    </div>
                    {shareLink && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
                            <button
                                onClick={handleCopyLink}
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: 999,
                                    border: 'none',
                                    background: '#fff',
                                    color: '#0071bc',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                {copyStatus === 'copied'
                                    ? 'Ссылка скопирована'
                                    : copyStatus === 'error'
                                        ? 'Ошибка копирования'
                                        : 'Скопировать ссылку'}
                            </button>
                        </div>
                    )}
                </div>

                <Section title="Основная информация">
                    <SectionRow label="Номер декларации" value={declaration.reg_number} />
                    {fsaLink && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{
                                fontSize: 11,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                color: '#0f172a',
                                opacity: 0.65,
                                fontWeight: 600,
                            }}>
                                Ссылка на декларацию
                            </span>
                            <a
                                href={fsaLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    fontSize: 16,
                                    color: '#0071bc',
                                    fontWeight: 500,
                                    textDecoration: 'underline',
                                    wordBreak: 'break-word',
                                }}
                            >
                                Открыть в реестре ФСА
                            </a>
                        </div>
                    )}
                    <SectionRow label="Статус декларации" value={getDeclarationStatus(declaration)} />
                    <SectionRow label="Основание" value={declaration.legal_basis} />
                    <SectionRow label="Тип декларации" value={declaration.decl_type} />
                    <SectionRow label="Схема декларирования" value={declaration.decl_schema} />
                    <SectionRow label="Дата регистрации" value={declaration.reg_date ? formatDate(declaration.reg_date) : null} />
                    <SectionRow label="Дата окончания" value={declaration.end_date ? formatDate(declaration.end_date) : null} />
                    <SectionRow label="Дата прекращения" value={declaration.termination_date ? formatDate(declaration.termination_date) : null} />
                    <SectionRow label="Причина прекращения" value={declaration.termination_reason} />
                </Section>

                <Section title="Заявитель">
                    <SectionRow label="Наименование" value={declaration.applicant_short_name} />
                    <SectionRow label="Полное наименование" value={declaration.applicant_full_name} />
                    <SectionRow label="ИНН" value={declaration.applicant_inn} />
                    <SectionRow label="ОГРН" value={declaration.applicant_ogrn} />
                    <SectionRow label="Тип" value={declaration.applicant_type} />
                    <SectionRow label="Адрес" value={declaration.applicant_address} />
                </Section>

                <Section title="Изготовитель">
                    <SectionRow label="Наименование" value={declaration.manufacturer_full_name} />
                    <SectionRow label="ИНН" value={declaration.manufacturer_inn} />
                    <SectionRow label="ОГРН" value={declaration.manufacturer_ogrn} />
                    <SectionRow label="Адрес" value={declaration.manufacturer_address} />
                </Section>

                <Section title="Продукция и документы">
                    <SectionRow label="Вид продукции" value={declaration.product_kind} />
                    <SectionRow label="Продукция" value={declaration.product_full_name} />
                    <SectionRow label="Код ТН ВЭД" value={declaration.tnved_code} />
                    <SectionRow label="Код ОКПД2" value={declaration.okpd2_code} />
                    <SectionRow label="Происхождение продукции" value={declaration.product_origin} />
                    <SectionRow label="Изготовлено в соответствии" value={declaration.tech_documents} />
                    <SectionRow label="Стандарты" value={declaration.standards} />
                    <SectionRow label="Документы заявителя" value={declaration.applicant_documents} />
                </Section>

                <Section title="Лаборатории и протоколы">
                    <SectionRow label="Названия лабораторий" value={declaration.lab_names} />
                    <SectionRow label="Номера лабораторий" value={declaration.lab_reg_numbers} />
                    <SectionRow label="Номера протоколов" value={declaration.protocol_numbers} />
                    <SectionRow label="Даты протоколов" value={declaration.protocol_dates} />
                </Section>

                {declaration.updated_at && (
                    <div style={{ color: '#6b7280', fontSize: 13, textAlign: 'right' }}>
                        Данные из реестра ФСА обновлены: {formatDate(declaration.updated_at)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeclarationDetailPage;
